import { useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import { ItemOrder } from 'hooks/useOrderB2c'
import { CartPreviewData } from 'types/progressiveDiscountTypes'
import { getCartPreview, getProductTiers } from 'services/progressiveDiscountService'
import { buildOriginCheckout, nextTierHint, NextTierHint } from 'utils/progressiveDiscount'

export type NextHint = NextTierHint

export interface UseCartProgressiveDiscountResult {
  preview: CartPreviewData | null
  loading: boolean
  error: boolean
  hasEditedPrices: boolean
  hint: NextHint | null
}

export function useCartProgressiveDiscount(items: ItemOrder[]): UseCartProgressiveDiscountResult {
  const [preview, setPreview] = useState<CartPreviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [hint, setHint] = useState<NextHint | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestSeqRef = useRef(0)

  const hasEditedPrices = items.some(i => i.imputed_value !== undefined && i.imputed_value !== '0.00')

  // Debounced fetch — estável entre renders via useRef
  const debouncedFetch = useRef(
    debounce(async (fetchItems: ItemOrder[], seq: number) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      const controller = new AbortController()
      abortControllerRef.current = controller

      const previewItems = fetchItems
        .filter(i => i.product?.id != null)
        .map(i => ({ product_id: i.product.id as number, quantity: i.quantity }))

      if (previewItems.length === 0) {
        if (seq === requestSeqRef.current) {
          setPreview(null)
          setError(false)
          setLoading(false)
        }
        return
      }

      setLoading(true)
      try {
        const result = await getCartPreview(
          { origin_checkout: buildOriginCheckout(), items: previewItems },
          controller.signal,
        )
        if (seq === requestSeqRef.current) {
          setPreview(result)
          setError(false)
        }
      } catch (e: any) {
        // Ignorar erros de cancelamento (AbortController)
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') {
          return
        }
        if (seq === requestSeqRef.current) {
          setError(true)
          setPreview(null)
        }
      } finally {
        if (seq === requestSeqRef.current) {
          setLoading(false)
        }
      }
    }, 300),
  ).current

  useEffect(() => {
    if (hasEditedPrices || items.length === 0) {
      setPreview(null)
      setError(false)
      setLoading(false)
      return
    }

    requestSeqRef.current += 1
    debouncedFetch(items, requestSeqRef.current)
  }, [items, hasEditedPrices]) // eslint-disable-line react-hooks/exhaustive-deps

  // Busca tiers para calcular o hint quando o preview mudar
  useEffect(() => {
    let cancelled = false

    const fetchHint = async () => {
      if (!preview || preview.applications.length === 0) {
        setHint(null)
        return
      }

      const firstApp = preview.applications[0]
      if (!firstApp || firstApp.eligible_items.length === 0) {
        setHint(null)
        return
      }

      try {
        const productId = firstApp.eligible_items[0].product_id
        const tiersData = await getProductTiers(productId)

        if (cancelled) return

        // Omitir hint quando count_mode=aggregated (critério por marca é ambíguo)
        if (tiersData.rule?.count_mode === 'aggregated') {
          setHint(null)
          return
        }

        const totalEligibleQty = firstApp.eligible_items.reduce((sum, i) => sum + i.quantity, 0)
        const computed = nextTierHint(tiersData.tiers, totalEligibleQty, tiersData.rule, tiersData.unit_price)
        if (!cancelled) setHint(computed)
      } catch {
        if (!cancelled) setHint(null)
      }
    }

    fetchHint()
    return () => {
      cancelled = true
    }
  }, [preview])

  // Limpeza no unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel()
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { preview, loading, error, hasEditedPrices, hint }
}
